/**
 * Dataset Service — wraps /api/admin/ai/datasets and unifies catalog + composed datasets
 * for action-bearing wizards (Phase 4.10 β2/β3/β5/β6 — WQ-237 onwards).
 *
 * Composed datasets are produced by WQ-236 (DatasetCompositionService).
 * Catalog entries are produced by WQ-182 (DataCatalog).
 *
 * `listAvailableDatasets` merges both sources for the AutoMLWizard's
 * dataset picker (Step 1).
 */

import { listCatalogEntries, getEntrySchema, type CatalogEntry } from './catalogService';

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

// === Types ===

export interface ComposedDataset {
  id: string;
  tenant_id: string | null;
  name: string;
  slug: string;
  composition_mode: string;
  status: string;
  parquet_row_count: number | null;
  column_schema: Array<{ name: string; dtype: string; [key: string]: unknown }> | null;
  last_compose_error: string | null;
  last_composed_at: string | null;
  lifecycle_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DatasetSchemaPayload {
  columns: Array<{ name: string; dtype: string; [key: string]: unknown }>;
  row_count: number | null;
}

export interface DatasetOption {
  id: string;
  name: string;
  source: 'catalog' | 'composed';
  row_count: number | null;
}

export interface AvailableDatasets {
  catalog: DatasetOption[];
  composed: DatasetOption[];
}

// === API Functions ===

export async function listComposedDatasets(
  authFetch: FetchFn,
  filters?: { tenant_id?: string; status?: string; composition_mode?: string }
): Promise<ComposedDataset[]> {
  const params = new URLSearchParams();
  if (filters?.tenant_id) params.set('tenant_id', filters.tenant_id);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.composition_mode) params.set('composition_mode', filters.composition_mode);
  const qs = params.toString();
  const res = await authFetch(`/api/admin/ai/datasets${qs ? '?' + qs : ''}`);
  if (!res.ok) throw new Error(`Failed to list composed datasets: ${res.status}`);
  return res.json();
}

export async function getComposedDatasetSchema(
  authFetch: FetchFn,
  datasetId: string
): Promise<DatasetSchemaPayload> {
  const res = await authFetch(`/api/admin/ai/datasets/${datasetId}/schema`);
  if (!res.ok) throw new Error(`Failed to get dataset schema: ${res.status}`);
  const data = await res.json();
  return {
    columns: Array.isArray(data?.column_schema) ? data.column_schema : [],
    row_count: typeof data?.parquet_row_count === 'number' ? data.parquet_row_count : null,
  };
}

/**
 * Unified dataset picker for action-bearing wizards. Fetches catalog entries
 * (filtered to active) and composed datasets (filtered to ready/non-error) in
 * parallel and returns them as two arrays of `DatasetOption`. Either source
 * failing in isolation does NOT fail the whole call — empty array is returned
 * for the failed source so the wizard can still show whatever loaded.
 */
export async function listAvailableDatasets(
  authFetch: FetchFn
): Promise<AvailableDatasets> {
  const [catalogResult, composedResult] = await Promise.allSettled([
    listCatalogEntries(authFetch, { is_active: true }),
    listComposedDatasets(authFetch, { status: 'ready' }),
  ]);

  const catalog: DatasetOption[] =
    catalogResult.status === 'fulfilled'
      ? catalogResult.value.map((c: CatalogEntry) => ({
          id: c.id,
          name: c.name,
          source: 'catalog' as const,
          row_count: null,
        }))
      : [];

  const composed: DatasetOption[] =
    composedResult.status === 'fulfilled'
      ? composedResult.value.map((d) => ({
          id: d.id,
          name: d.name,
          source: 'composed' as const,
          row_count: d.parquet_row_count,
        }))
      : [];

  return { catalog, composed };
}

/**
 * Source-aware schema fetch. Wizard knows whether the user picked a catalog
 * entry or a composed dataset (from `DatasetOption.source`); this helper
 * routes to the right endpoint. Both endpoints return objects with a
 * `columns` array of `{name, dtype}` (catalog: schema_snapshot.columns;
 * composed: column_schema). The shapes are normalized to
 * `{ columns: [...], row_count }`.
 */
export async function getDatasetSchema(
  authFetch: FetchFn,
  datasetId: string,
  source: 'catalog' | 'composed'
): Promise<DatasetSchemaPayload> {
  if (source === 'composed') {
    return getComposedDatasetSchema(authFetch, datasetId);
  }
  const raw = (await getEntrySchema(authFetch, datasetId)) as Record<string, unknown>;
  const columns = Array.isArray(raw?.columns)
    ? (raw.columns as Array<{ name: string; dtype: string; [key: string]: unknown }>)
    : [];
  const row_count = typeof raw?.record_count === 'number' ? (raw.record_count as number) : null;
  return { columns, row_count };
}
